import posthog from 'posthog-js';
import { initAnalytics } from './analytics';
import { isInternalTraffic } from './internalTraffic';
import { handoffBootstrap } from './handoff';

/**
 * Wire PostHog (https://posthog.com) as the product-analytics + client-error provider.
 *
 * Gated entirely on `REACT_APP_POSTHOG_KEY`: with no key (dev, CI, self-host without a project)
 * this no-ops and the analytics layer stays inert, so behaviour is unchanged until a key is set.
 * PostHog's free tier (1M events/mo, no credit card) is the chosen provider, see ADR-0007.
 *
 * `person_profiles: 'identified_only'` keeps anonymous events from creating person profiles, which
 * both protects privacy and keeps usage well inside the free tier. Errors during init never bubble
 * up, analytics must never break the app boot.
 *
 * @param {object} [env=process.env] Injectable for tests.
 * @returns {boolean} true when PostHog was initialised, false when no key was configured.
 */
export function initProductAnalytics(env = process.env) {
  const key = env.REACT_APP_POSTHOG_KEY;
  if (!key) {
    return false;
  }
  // Our own browsers, marked once with ?internal=1, see utils/internalTraffic.js.
  if (isInternalTraffic()) {
    // eslint-disable-next-line no-console
    console.info('FlowGate analytics off for this browser (internal). Re-enable with ?internal=0');
    return false;
  }
  const bootstrap = handoffBootstrap();
  try {
    posthog.init(key, {
      // In production this is the first-party reverse proxy (ph.flowgate.co.uk), so events are
      // not dropped by the ad blockers that a great many people run, requests to posthog.com
      // are on every common blocklist, and the traffic they silently swallowed never appeared
      // as a gap, only as smaller numbers.
      //
      // The default is the EU region because that is where project 273156 lives (ADR: PostHog
      // project is Flowgate2, EU). It said `us.` until 2026-09-24, which Amplify happened to
      // override, so anyone running locally with a key was posting to the wrong continent.
      api_host: env.REACT_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
      // Where PostHog's own UI lives, which is NOT the proxy. Without this the toolbar and
      // every "view in PostHog" link would point at ph.flowgate.co.uk, which serves ingestion
      // and not an app.
      ui_host: env.REACT_APP_POSTHOG_UI_HOST || 'https://eu.posthog.com',
      person_profiles: 'identified_only',
      // FlowGate is a react-router SPA: without this, only the initial page load is counted as a
      // $pageview and client-side route changes are missed. 'history_change' wires the History-API
      // listener so each navigation emits a $pageview.
      capture_pageview: 'history_change',
      // No cookies and no localStorage, so no PECR/ePrivacy consent banner is needed (UK/EU) and
      // nothing outlives the tab. sessionStorage rather than 'memory' because memory did not
      // survive a page reload: every refresh minted a new anonymous person, which broke any
      // funnel longer than one page load. Same posture as utils/attribution.js.
      // Returning visitors are still new people, that genuinely needs a cookie and the consent
      // banner that comes with it.
      persistence: 'sessionStorage',
      // Continue the person and session the landing page started, when it handed them over.
      // Ignored by PostHog when this tab already has stored state, which is what we want: the
      // link's ids matter on arrival, not on every later reload.
      ...(bootstrap ? { bootstrap } : {}),
      // PostHog error tracking replaces Sentry on the frontend: uncaught exceptions and
      // unhandled rejections are captured as $exception events, grouped in PostHog's Errors tab.
      capture_exceptions: true,
      // Session replay is switched on server-side (per-project setting); the client only needs
      // to be ready and safe when it is. Every typed input is masked regardless of type (so a
      // signed-in user's own text is never recorded either), `.ph-mask` is available for any
      // element that needs the same treatment case-by-case, and `.ph-no-capture` excludes a
      // whole element from recording, used on the elements that render OTHER PEOPLE's data
      // (a merchant's customers): the Submissions table, booking list rows, destination summaries
      // and the API key reveal.
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        // Inputs are masked by default (login, billing, customer data). The assistant's
        // "What should your flow do?" box is the one input we want to read back in replays,
        // because what people ask for is the product signal; it opts out with data-ph-unmask.
        maskInputFn: (text, element) => (element && element.closest && element.closest('[data-ph-unmask]')
          ? text
          : '*'.repeat(text.length)),
        maskTextSelector: '.ph-mask',
        blockSelector: '.ph-no-capture',
      },
    });
    initAnalytics({
      capture: (event, props) => posthog.capture(event, props),
      // `setOnceTraits` (attribution, see utils/attribution.js) goes in PostHog's third
      // identify argument, which it applies as `$set_once`: only the first value ever recorded
      // for a person sticks, even across repeat sign-ins. Omitted when there is none, so this
      // stays a plain 2-arg identify() everywhere else.
      identify: (id, traits, setOnceTraits) => (setOnceTraits
        ? posthog.identify(id, traits, setOnceTraits)
        : posthog.identify(id, traits)),
      reset: () => posthog.reset(),
    });
    return true;
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('PostHog init failed', e);
    }
    return false;
  }
}
